/**
 * Converts a given string into a URL-friendly slug.
 *
 * This function performs the following transformations:
 * - Converts the string to lowercase.
 * - Replaces special characters with their closest ASCII equivalents.
 * - Replaces spaces with dashes.
 * - Replaces '&' with '-and-'.
 * - Removes all non-word characters except dashes.
 * - Trims leading and trailing dashes.
 * - Removes duplicated dashes.
 * - Limits the length of the resulting slug to `maxLength`.
 * - Pads the resulting slug to `minLength` with underscores if necessary.
 *
 * @param {string} string - The input string to be slugified.
 * @param {number} [minLength=0] - The minimum length of the resulting slug. If the slug is shorter, it will be padded with underscores.
 * @param {number} [maxLength=Infinity] - The maximum length of the resulting slug. The slug will be truncated if it exceeds this length.
 * @returns {string} The slugified version of the input string.
 */
function slugify(string, minLength = 0, maxLength = Infinity) {
    const specialCharsRegex = /[àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/,:;]+/g;
    const normalChars = "aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz-----";
    return string
        .toString()
        .toLowerCase()
        .replace(specialCharsRegex, (specialMatch) =>
            specialMatch.split("").reduce((sanitizedChars, specialChar) => {
                const normalCharIndex = specialCharsRegex.source.indexOf(specialChar) - 1;
                return sanitizedChars + normalChars.charAt(normalCharIndex);
            }, "")
        )
        .replace(/\s+/g, "-") // Replace spaces with dashes
        .replace(/&/g, "-and-") // Replace & with '-and-'
        .replace(/[^\w-]+/g, "") // Remove all non-word characters
        .replace(/(?:^-+|-+$)/g, "") // Trim dashes
        .replace(/-{2,}/g, "-") // Remove duplicated dashes
        .slice(0, maxLength) // Limit the length to maxLength
        .padEnd(minLength, "_"); // Apply minimum length padding
}

module.exports = slugify;
